import { AfterViewInit, Component, EventEmitter, inject, OnInit, Output } from '@angular/core'
import { NgClass, NgForOf, NgIf } from '@angular/common'
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { TypeReferenceDto } from '../../../sunleon/data-access/http/dto/type-reference-dto'
import { ReferenceService } from '../../../sunleon/data-access/http/reference.service'
import { ZoneMeasures } from '../../model/zone-measures'
import { GeomapDomainService } from '../../domain/geomap.domain'
import { CalepinageService } from '../../data-access/calepinage.service'
import { CustomValidators } from '../../../shared/validators/custom-validators'
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'

@Component({
  selector: 'app-form-zone3',
  templateUrl: './form-zone3.component.html',
  styleUrls: ['./form-zone3.component.scss'],
  standalone: true,
  imports: [NgIf, FormsModule, NgForOf, ReactiveFormsModule, NgClass],
})
export class FormZone3Component implements OnInit, AfterViewInit {
  @Output()
  formReady: EventEmitter<FormGroup> = new EventEmitter<FormGroup>()

  private readonly geomapDomainService = inject(GeomapDomainService)
  private readonly calepinageService = inject(CalepinageService)
  readonly measuresZone3$ = this.geomapDomainService.getAllMeasuresForZone$(2)

  azimutLabel = "Azimut (compris entre -180 et 180, SUD = 0)"

  zone3Form: FormGroup
  typePoses: TypeReferenceDto[] = []
  typeToitures: TypeReferenceDto[] = []
  typePlaces: TypeReferenceDto[] = []
  typeParkings: TypeReferenceDto[] = []

  onRadioClickZone3(code: string | null, controleName: string) {
    this.zone3Form.get(controleName).setValue(code)
    this.geomapDomainService.selectTypePoseOutils(2)
    this.geomapDomainService.selectionBarreOutils()
    this.geomapDomainService.selectionDessinOutilsSeulementZone()
  }

  ngAfterViewInit(): void {
    this.measuresZone3$.subscribe((zoneMeasures: ZoneMeasures) => {
      this.zone3Form.get('surfaceZone3').patchValue(zoneMeasures.surface === 0 ? null : zoneMeasures.surface)
      this.zone3Form.get('azimutZone3').patchValue(zoneMeasures.azimuth === 0 ? null : zoneMeasures.azimuth)
      this.zone3Form.get('polygonsZone3').patchValue(zoneMeasures.polygons);
      this.zone3Form.get('azimutCoordinateZone3').patchValue(zoneMeasures.azimutCoordinates);
      if ((zoneMeasures.polygons?.length ?? 0) > 0 && this.zone3Form.get('puissanceChoisieZone3')?.value != null) {
        this.zone3Form.get('puissanceChoisieZone3')?.patchValue(null, { emitEvent: false })
      }
      this.updateZoneValueValidators()
      this.zone3Form.updateValueAndValidity({ emitEvent: false })
    })

    this.calepinageService.registerZoneAutoTrigger(2, this.zone3Form, {
      typePose: 'typePoseZone3',
      typeToiture: 'typeToitureZone3',
      inclinaison: 'inclinaisonZone3',
      typeVehiculeParking: 'typeVehiculeParkingZone3',
      typePlaceParking: 'typePlaceParkingZone3',
      orientation: 'orientationTerrasseZone3',
    })
  }

  constructor(private formBuilder: FormBuilder, private referenceService: ReferenceService, private modalService: NgbModal) {
    this.zone3Form = this.formBuilder.group({
      typePoseZone3: [null],
      surfaceZone3: [null, [Validators.pattern('^\\d+(\\.\\d{1,2})?$')]],
      azimutZone3: [null, [Validators.pattern('^-?(180|\\d{1,2}|1[0-7]\\d)$')]],
      typeToitureZone3: [''],
      orientationTerrasseZone3: ['SUD'],
      pvReadyZone3: [false],
      inclinaisonZone3:  [null, [Validators.pattern('^\\d+$'), this.inclinaisonValidator()]],
      typeVehiculeParkingZone3: [''],
      typePlaceParkingZone3: [''],
      puissanceChoisieZone3: [null, [Validators.pattern('^\\d+(\\.\\d{1,2})?$')]],
      polygonsZone3: [null],
      azimutCoordinateZone3: [null]
    }, { validators: this.zoneSurfaceOrPowerValidator() })

    this.initTypePoseZone(
      'typePoseZone3',
      'deposeCandelabre',
      'poseCandelabre',
      'eclairageSousOmbriere',
      'descenteEpAlu',
      'nbAbattageArbres',
      'mlClotureSol',
      'nbPortailsSol',
      "mlGardeCorpsProvisoire",
      "mlLigneDeVie",
      "nbEchelleCrinoline",
      'surfaceZone3',
      'azimutZone3',
      'typeToitureZone3',
      'pvReadyZone3',
      'inclinaisonZone3',
      'typeVehiculeParkingZone3',
      'typePlaceParkingZone3',
      'puissanceChoisieZone3'
    )

    this.zone3Form.get('puissanceChoisieZone3')?.valueChanges.subscribe(() => {
      this.updateZoneValueValidators()
      this.zone3Form.updateValueAndValidity({ emitEvent: false })
    })
  }

  ngOnInit(): void {
    this.referenceService.getTypePose().subscribe((data) => {
      this.typePoses = data
    })
    this.referenceService.getTypeToiture().subscribe((data) => {
      this.typeToitures = data.filter(t => t.code !== 'BAC_ACIER_SEC')
    })
    this.referenceService.getTypeParking().subscribe((data) => {
      this.typeParkings = data
    })
    this.referenceService.getTypePlace().subscribe((data) => {
      this.typePlaces = data
    })
    this.updateZoneValueValidators()
    this.zone3Form.updateValueAndValidity({ emitEvent: false })
    this.formReady.emit(this.zone3Form)
  }

  initTypePoseZone(
    typePose: string,
    deposeCandelabre: string,
    poseCandelabre: string,
    eclairageSousOmbriere: string,
    descenteEpAlu: string,
    nbAbattageArbres: string,
    mlClotureSol: string,
    nbPortailsSol: string,
    mlGardeCorpsProvisoire: string,
    mlLigneDeVie: string,
    nbEchelleCrinoline: string,
    surfaceZone: string,
    azimutZone: string,
    typeToitureZone: string,
    pvReadyZone: string,
    inclinaisonZone: string,
    typeVehiculeParkingZone: string,
    typePlaceParkingZone: string,
    puissanceChoisieZone: string
  ) {

    this.zone3Form.get(typePose).valueChanges.subscribe((typePoseZone) => {
      const constSurfaceZone = this.zone3Form.get(surfaceZone)
      const constAzimutZone = this.zone3Form.get(azimutZone)
      const constTypeToitureZone = this.zone3Form.get(typeToitureZone)
      const constPvReadyZone = this.zone3Form.get(pvReadyZone)
      const constInclinaisonZone = this.zone3Form.get(inclinaisonZone)
      const constTypeVehiculeParkingZone = this.zone3Form.get(typeVehiculeParkingZone)
      const constTypePlaceParkingZone = this.zone3Form.get(typePlaceParkingZone)

      this.geomapDomainService.currentZone.typeVehiculeParking = '';
      this.geomapDomainService.activateMeasureAreaTool()

      if (typePoseZone) {
        this.updateZoneValueValidators()

        if (typePoseZone === 'TOITURE') {
          constTypeToitureZone.setValidators([CustomValidators.requiredIfControlHasValue(typePose)])
          constPvReadyZone.setValidators([CustomValidators.requiredIfControlHasValue(typePose)])
          constInclinaisonZone.setValidators([
            Validators.required,
            Validators.pattern('^\\d+$'),
            this.inclinaisonValidator()
          ])        
          constTypeToitureZone.reset()
          constPvReadyZone.reset()
          constInclinaisonZone.reset()

          constTypeVehiculeParkingZone.setValidators([Validators.nullValidator])
          constTypePlaceParkingZone.setValidators([Validators.nullValidator])
          constTypeVehiculeParkingZone.reset()
          constTypePlaceParkingZone.reset()
        }
        if (typePoseZone === 'OMBRIERE') {
          constTypeVehiculeParkingZone.setValidators([CustomValidators.requiredIfControlHasValue(typePose)])
          constTypeVehiculeParkingZone.reset()
          constTypePlaceParkingZone.reset()

          constTypeToitureZone.setValidators([Validators.nullValidator])
          constPvReadyZone.setValidators([Validators.nullValidator])
          constInclinaisonZone.setValidators([Validators.nullValidator])

          constTypeToitureZone.reset()
          constPvReadyZone.reset()
          constInclinaisonZone.reset()
        }

        if (typePoseZone === 'SOL') {
          constTypeToitureZone.setValidators([Validators.nullValidator])
          constPvReadyZone.setValidators([Validators.nullValidator])
          constInclinaisonZone.setValidators([Validators.nullValidator])
          constTypeVehiculeParkingZone.setValidators([Validators.nullValidator])
          constTypePlaceParkingZone.setValidators([Validators.nullValidator])

          constTypeToitureZone.reset()
          constPvReadyZone.reset()
          constInclinaisonZone.reset()
          constTypeVehiculeParkingZone.reset()
          constTypePlaceParkingZone.reset()
        }
      } else {
        constSurfaceZone.setValidators([Validators.pattern('^\\d+(\\.\\d{1,2})?$')])
        constAzimutZone.setValidators([Validators.pattern('^-?(180|\\d{1,2}|1[0-7]\\d)$')])
        constTypeToitureZone.setValidators([Validators.nullValidator])
        constPvReadyZone.setValidators([Validators.nullValidator])
        constInclinaisonZone.setValidators([Validators.nullValidator])
        constTypeVehiculeParkingZone.setValidators([Validators.nullValidator])
        constTypePlaceParkingZone.setValidators([Validators.nullValidator])

        constSurfaceZone.reset()
        constAzimutZone.reset()
        constTypeToitureZone.reset()
        constPvReadyZone.reset()
        constInclinaisonZone.reset()
        constTypeVehiculeParkingZone.reset()
        constTypePlaceParkingZone.reset()
      }

      constSurfaceZone.updateValueAndValidity({ emitEvent: false })
      constAzimutZone.updateValueAndValidity({ emitEvent: false })
    })

    this.zone3Form.get(typeVehiculeParkingZone).valueChanges.subscribe((typeVehiculeParking) => {
      const constTypeVehiculeParkingZone = this.zone3Form.get(typeVehiculeParkingZone)
      const constTypePlaceParkingZone = this.zone3Form.get(typePlaceParkingZone)
      constTypeVehiculeParkingZone.setValidators([CustomValidators.requiredIfControlHasValue(typePose)])
      if (typeVehiculeParking === 'VL') {
        constTypePlaceParkingZone.setValidators([CustomValidators.requiredIfControlHasValue(typePose)])
      } else {
        constTypePlaceParkingZone.setValidators([Validators.nullValidator])
        constTypePlaceParkingZone.reset()
      }
    })

    this.zone3Form.get(typeToitureZone).valueChanges.subscribe((typeToiture) => {
      const constAzimutZone = this.zone3Form.get(azimutZone)
      this.zone3Form.get(inclinaisonZone).updateValueAndValidity();
      if (typeToiture === 'BETON' || typeToiture === 'TAN_ETANCHE') {
        this.azimutLabel = "Azimut (compris entre -90 et 90, SUD = 0)"
        this.updateZoneValueValidators()
      } else {
        this.azimutLabel = "Azimut (compris entre -180 et 180, SUD = 0)"
        this.updateZoneValueValidators()
      }
      constAzimutZone.updateValueAndValidity({ emitEvent: false })
    })

    this.zone3Form.get(azimutZone).valueChanges.subscribe((azimut) => {
      const constAzimutZone = this.zone3Form.get(azimutZone)
      const constTypeToitureZone = this.zone3Form.get(typeToitureZone)
      if (constTypeToitureZone.value === 'BETON' || constTypeToitureZone.value === 'TAN_ETANCHE') {
        this.azimutLabel = "Azimut (compris entre -90 et 90, SUD = 0)"
        this.updateZoneValueValidators()
      } else {
        this.azimutLabel = "Azimut (compris entre -180 et 180, SUD = 0)"
        this.updateZoneValueValidators()
      }
      constAzimutZone.updateValueAndValidity({ emitEvent: false })
    })
  }

  onPuissanceChoisieFocus(event: FocusEvent): void {
    if (!this.hasDrawnSurface()) {
      return
    }

    const input = event.target as HTMLInputElement | null
    input?.blur()
    const modalRef = this.modalService.open(ConfirmationModalComponent)
    modalRef.componentInstance.message = `L'ajout d'une puissance dans "Puissance choisie" entrainera la suppression des surfaces dessinées précédemment.`
    modalRef.componentInstance.confirmation = 'Voulez vous continuer ?'
    modalRef.result.then((confirmed: boolean) => {
      if (!confirmed) {
        return
      }
      this.geomapDomainService.clearZoneDrawings(2)
      this.zone3Form.patchValue({
        surfaceZone3: null,
        polygonsZone3: null,
      }, { emitEvent: false })
      this.updateZoneValueValidators()
      setTimeout(() => input?.focus())
    })
  }

  selectVehicule(typeVehicule: string, typePlace: string | null): void {
    const oldTypeVehicule = this.zone3Form.get('typeVehiculeParkingZone3')?.value;
    const oldTypePlace = this.zone3Form.get('typePlaceParkingZone3')?.value;

    const newSelection = `${typeVehicule}_${typePlace}`;
    const oldSelection = `${oldTypeVehicule}_${oldTypePlace}`;
    
    if (newSelection !== oldSelection && this.geomapDomainService.currentZone.vectorLayer.getSource().getFeatures().length > 0) {
      const modalRef = this.modalService.open(ConfirmationModalComponent);
      modalRef.result.then((confirmed : any) => {
        if (confirmed) {
          this.geomapDomainService.resetDessinModeValorisation();
          this.applySelection(typeVehicule, typePlace);
        }
      });
    } else {
      this.applySelection(typeVehicule, typePlace);
    }
  }
    
  private applySelection(typeVehicule: string, typePlace: string | null): void {
    this.zone3Form.get('typeVehiculeParkingZone3')?.setValue(typeVehicule);
    this.zone3Form.get('typePlaceParkingZone3')?.setValue(typePlace);
    this.geomapDomainService.currentZone.typeVehiculeParking = this.zone3Form.get('typeVehiculeParkingZone3')?.value + '_' + this.zone3Form.get('typePlaceParkingZone3')?.value;
    this.geomapDomainService.activateMeasureAreaTool()
  }
  
  isVLSelected(place: string): boolean {
    return this.zone3Form.get('typeVehiculeParkingZone3')?.value === 'VL' &&
           this.zone3Form.get('typePlaceParkingZone3')?.value === place;
  }

  inclinaisonValidator() {
  return (control: any) => {
    if (!control || !this.zone3Form) return null;

    const inclinaison = control.value;
    const typeToiture = this.zone3Form.get('typeToitureZone3')?.value;

    if (inclinaison == null || inclinaison === '') return null;

    if (typeToiture === 'BETON' || typeToiture === 'TAN_ETANCHE') {
      if (inclinaison > 3) {
        return { inclinaisonMax: true };
      }
    }

    if (typeToiture === 'BAC_ACIER_SEC_EXISTANT' || typeToiture === 'BAC_ACIER_SEC_NEUF') {
      if (inclinaison <= 3) {
        return { inclinaisonMin: true };
      }
    }

    return null;
  };
}

private updateZoneValueValidators(): void {
  const typePose = this.zone3Form.get('typePoseZone3')?.value
  const puissanceChoisie = this.zone3Form.get('puissanceChoisieZone3')?.value
  const hasPower = puissanceChoisie !== null && puissanceChoisie !== undefined && puissanceChoisie !== ''
  const surfaceControl = this.zone3Form.get('surfaceZone3')
  const azimutControl = this.zone3Form.get('azimutZone3')
  const typeToiture = this.zone3Form.get('typeToitureZone3')?.value

  surfaceControl?.setValidators(hasPower || !typePose ? [Validators.pattern('^\\d+(\\.\\d{1,2})?$')] : [Validators.required, Validators.pattern('^\\d+(\\.\\d{1,2})?$')])

  const azimutPattern =
    typeToiture === 'BETON' || typeToiture === 'TAN_ETANCHE'
      ? '^-?(90|[0-8]?[0-9])$'
      : '^-?(180|\\d{1,3}|1[0-7]\\d)$'

  azimutControl?.setValidators(hasPower || !typePose ? [Validators.pattern(azimutPattern)] : [Validators.required, Validators.pattern(azimutPattern)])
  surfaceControl?.updateValueAndValidity({ emitEvent: false, onlySelf: true })
  azimutControl?.updateValueAndValidity({ emitEvent: false, onlySelf: true })
}

private zoneSurfaceOrPowerValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const typePose = control.get('typePoseZone3')?.value
    if (!typePose) {
      return null
    }
    const surface = control.get('surfaceZone3')?.value
    const puissanceChoisie = control.get('puissanceChoisieZone3')?.value
    const hasSurface = surface !== null && surface !== undefined && surface !== ''
    const hasPower = puissanceChoisie !== null && puissanceChoisie !== undefined && puissanceChoisie !== ''
    return hasSurface || hasPower ? null : { surfaceOrPowerRequired: true }
  }
}

private hasDrawnSurface(): boolean {
  const polygons = this.zone3Form.get('polygonsZone3')?.value as unknown[] | null
  const surface = this.zone3Form.get('surfaceZone3')?.value
  return !!(polygons && polygons.length > 0) || !!surface
}
}

